import React, { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from 'src/components/ui/alert-dialog'
import Login from './login'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { createFolderInOneDrive, deleteItemFromOneDrive, getDocumentEditLink, getDocumentPreview, getDocumentsByNameSubstring, getDriveItems, getFileContentAsBase64, getOneDriveFiles, uploadFileToOneDrive } from 'src/graph';
import { msalConfig, requestScopes } from 'src/authConfig';
import { useMS } from './context/MSContext';
import toast from 'react-hot-toast';
import axios from 'src/axios/axios';
import useAuth from 'src/hooks/useAuth';
import { Button } from 'src/components/ui/button';
import { CloudUploadIcon, InfoIcon, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CONFIG } from 'src/config';

type P = {
    itemId?: string
    docId?: number
    trigger?: React.ReactNode
    onReload?: () => void;
}

type UTF = {
    selected_documents: string[]
    onedrivedocuments:any[]
    onSuccess?: () => void;
}

const UploadToFitdoc = ({selected_documents, onedrivedocuments, onSuccess}:UTF) => {
    const { auth } = useAuth()
    const { accessToken } = useMS()
    const [removeFromDrive, setremoveFromDrive] = useState(true);

    async function handleUpdateDocuments () {
        try {
            toast.loading('Documenten uploaden in FITdoc...', { id:'updateDocuments' });

            const updatePromises = selected_documents.map(async (id) => {
                const fileOneDrive = onedrivedocuments.find(d => d.id === id);
                const fileNameOneDrive = fileOneDrive?.name || '';
                const docid = fileNameOneDrive.match(/\[(.*?)\]/)[1];
                const startIndex = fileNameOneDrive.indexOf('] - ') + 4; // +4 to include the ' - ' part
                const fileName = fileNameOneDrive.substring(startIndex);
                const base64String = await getFileContentAsBase64(accessToken, fileOneDrive.id);

                const response = await axios.post(`documents/addversion`, {
                    token: auth.data?.token,
                    filename: fileName,
                    file: base64String,
                    docid: docid
                });

                if (response.data.errorcode !== 0) {
                    throw new Error(`user ${id} updating failed`);
                }

                // Optionally remove from OneDrive if specified
                if (removeFromDrive) {
                    await deleteItemFromOneDrive(accessToken, fileOneDrive.id);
                }
            });

            await Promise.all(updatePromises);

            onSuccess && onSuccess();
            toast.remove('updateDocuments');
        } catch (error:any) {
            if(error.message === 'The resource you are attempting to access is locked'){
                toast.error('Het document is locked, gelieve het document te sluiten of later opnieuw te proberen.', { id:'updateDocuments', duration:7500 })
                return;
            }
            console.log(error);
            toast.error('Document updaten in FitDoc mislukt', { id:'updateDocuments' })
        }
    }

    return (
        <AlertDialog>
            <AlertDialogTrigger asChild><Button disabled={selected_documents.length === 0} className='gap-2'><CloudUploadIcon className='w-4 h-4 group-hover:text-primary'/>Uploaden in FitDoc</Button></AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Let op! Documenten uploaden naar FitDoc</AlertDialogTitle>
                    <AlertDialogDescription>U staat op het punt om {selected_documents.length} document{selected_documents.length > 1 && 'en'} te overschrijven</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <AlertDialogCancel>Annuleren</AlertDialogCancel>
                    <AlertDialogAction onClick={()=>{handleUpdateDocuments()}}>Uploaden</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

const EditPreview = ({itemId, docId, trigger, onReload}:P) => {
    const { auth } = useAuth()
    const { instance, accounts } = useMsal();
    const { accessToken, setaccessToken, setaccount, setfitdocfolder, fitdocfolder, settmpfolder, tmpfolder } = useMS()

    const [open_dialog, setopen_dialog] = useState(false);

    const [opened_onedrive_document, setopened_onedrive_document] = useState<any>(undefined);

    async function RequestProfileData() {
        const response = await instance.acquireTokenSilent({...requestScopes, account: accounts[0]})
        setaccount(response.account)
        setaccessToken(response.accessToken)
    }
    useEffect(()=>{ 
        if(!open_dialog) return;
        
        RequestProfileData()

        return () => {
            toast.remove('addDocument')
            toast.remove('createFitdocFolder')
            toast.remove('gd')
        }
    }, [open_dialog])

    async function handleAddDocument (docid:number, parentId:string) {
        try {
            //toast.loading('Document uploaden...', {id:'addDocument'})
            const { data:d } = await axios.get(`documents/get?docid=${docid}`)
            if(d.errorcode!==0){ throw new Error(d.message) }

            const filename = `[${docid}] - ${d.object.filename}`; // filename of document
            const base64File = d.object.file; // base64 of document

            const fileBlob = base64ToBlob(base64File, 'application/octet-stream');
            const file = new File([fileBlob], filename);

            console.log("Uploading to ", parentId, tmpfolder)

            const res = await uploadFileToOneDrive(accessToken, file, parentId)
            //toast.success('Document toegevoegd', {id:'addDocument'})
            return res
        } catch (error) {
            console.log(error);
            toast.error('Document uploaden mislukt', {id:'addDocument'})
        } 
    }

    useQuery({
        queryKey: [`reload-preview`],
        queryFn: async () => {
            setTimeout(async ()=>{ await findDocumentInOneDrive()}, 1000)
            
            return null
        },
        enabled: !!open_dialog && !!tmpfolder
    })

    async function findDocumentInOneDrive() {
        try {
            toast.loading('Document ophalen...', { id:'findDocumentInOneDrive', position:'bottom-center' })
            if(docId){
                // Search document that includes docId in name
                let foundDocs = []
                try { foundDocs = await getDocumentsByNameSubstring(accessToken, docId, tmpfolder.id) } catch (e) { console.log(e) }
                if(foundDocs && foundDocs?.length < 1){
                    console.log("No docs found with DOCID in OD, creating...")
                    // No document found with docId
                    // Create the document
                    const d = await handleAddDocument(docId, tmpfolder.id)

                    toast.loading('Document ophalen...', {id:'gd', duration:5000})

                    try {
                        if(d.id){ 
                            const previewUrls = await getDocumentPreview(accessToken, d.id)
                            //const editUrl = await getDocumentEditLink(accessToken, d.id)
                            setopened_onedrive_document({...d, getUrl:previewUrls.getUrl});
                        } 
                    } catch (e) { console.log(e) }

                    toast.remove('gd')
                    
                }
                else {

                    console.log(foundDocs)
                    //const editUrl = await getDocumentEditLink(accessToken, foundDocs[0].id)

                    const previewUrls = await getDocumentPreview(accessToken, foundDocs[0].id)
                    setopened_onedrive_document({...foundDocs[0], getUrl:previewUrls.getUrl});
                }
            }
            if(itemId){
                // Search document by id from OneDrive
            }
        } catch (e) {
            toast.error('Document ophalen mislukt', { id:'findDocumentInOneDrive', position:'bottom-center' })
        } finally {
            toast.remove('findDocumentInOneDrive')
        }
    }

    useEffect(()=>{
        if(open_dialog && tmpfolder && !opened_onedrive_document){
            console.log("Search document in OD", tmpfolder.id)
            findDocumentInOneDrive()
        }
    }, [tmpfolder])

    useQuery({
        queryKey: [`onedrive-files`],
        queryFn: async () => {
            const odf = await getOneDriveFiles(accessToken);
            const fitdocFolder = odf.value.find((f:any) => f.name === CONFIG.onedrive.fitdoc_foldername)
            const parentId = odf.value[0].parentReference.id

            if(!fitdocFolder){ 
                toast.loading('FITdoc map aanmaken...', {id:'createFitdocFolder'})
                const folder = await createFolderInOneDrive(accessToken, parentId, CONFIG.onedrive.fitdoc_foldername)
                const tmp = await createFolderInOneDrive(accessToken, folder.id, CONFIG.onedrive.fitdoc_tmp_foldername)
                toast.success('FITdoc map aangemaakt', {id:'createFitdocFolder'})
                setfitdocfolder(folder)
                settmpfolder(tmp)
                return []
            }

            const fdItems = await getDriveItems(accessToken, fitdocFolder.id)
            if(fdItems.value){
                const tmp = fdItems.value.find((f:any)=>f.name === CONFIG.onedrive.fitdoc_tmp_foldername)
                if(!tmp){
                    const tmp = await createFolderInOneDrive(accessToken, fitdocFolder.id, CONFIG.onedrive.fitdoc_tmp_foldername)
                    settmpfolder(tmp)
                } else { settmpfolder(tmp) }
            }

            setfitdocfolder(fitdocFolder)

            return odf.value
        },
        enabled: !!accessToken && open_dialog,
    })
    
    return (
        <AlertDialog open={open_dialog} onOpenChange={(o)=>{setopen_dialog(o)}}>
            <AlertDialogTrigger asChild>{trigger ? trigger: <button>Open</button>}</AlertDialogTrigger>
            <AlertDialogContent overlayClassName='bg-black/60' className='w-full max-w-[100dvw] h-full max-h-[95dvh] overflow-auto bottom-0 rounded-b-none p-0'>
                <AuthenticatedTemplate>
                    {opened_onedrive_document 
                    ?
                    <div className='flex-1'>
                        <div className='flex items-center justify-between p-4 border-b'>
                            <div className='flex items-center gap-2'>
                                <UploadToFitdoc selected_documents={[opened_onedrive_document.id]} onedrivedocuments={[opened_onedrive_document]} onSuccess={()=>{setopen_dialog(false); toast.success('Nieuwe versie toegevoegd', { id:'add-document' }); onReload&&onReload() }}/>
                            </div>

                            <div className='space-x-2'>
                                <Button onClick={()=>{setopen_dialog(false)}} variant='outline'>Sluiten</Button>
                            </div>
                        </div>
                        <div className='py-1 bg-yellow-100 border-b border-yellow-800/20'>
                            <div className='container flex items-center justify-center gap-4'>
                                <p className='text-xs'><span className='mr-2 font-medium'>Alleen-lezen</span> Dit bestand is geopend in een lees-weergave. </p>
                                <button onClick={()=>{window.open(opened_onedrive_document.webUrl)}} className='text-xs border border-solid border-yellow-800/20 rounded-sm py-0.5 px-2 font-medium bg-yellow-200 hover:bg-yellow-300'>Document bewerken</button>
                            </div>
                        </div>
                        <div className='w-full h-full bg-neutral-100'>
                            <iframe  className='w-full h-full' src={`${opened_onedrive_document.getUrl}&nb=true`} sandbox="allow-same-origin allow-scripts allow-popups allow-forms" frameBorder="0">Dit is een ingesloten <a target="_blank" href="https://office.com">Microsoft Office</a>-documentbestand dat mogelijk is gemaakt met <a target="_blank" href="https://office.com/webapps">Office</a>.</iframe>                            
                        </div> 
                    </div>
                    : <p className='flex items-center gap-2 font-medium'></p>}
                </AuthenticatedTemplate>
                <UnauthenticatedTemplate>
                    <Login />
                </UnauthenticatedTemplate>
            </AlertDialogContent>
        </AlertDialog>
    )
}

export default EditPreview

function base64ToBlob(base64: string, contentType: string): Blob {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
}